/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiIcon } from '../icon';
import { useEuiTheme } from '../../services';
import { euiExpressionStyles, euiExpressionDescriptionStyles, euiExpressionValueStyles, euiExpressionIconStyles } from './expression.styles';
export var COLORS = ['subdued', 'primary', 'success', 'accent', 'warning', 'danger'];
export var EuiExpression = ({
  className,
  description,
  descriptionProps,
  value,
  valueProps,
  color = 'success',
  uppercase = true,
  isActive = false,
  display = 'inline',
  descriptionWidth = '20%',
  onClick,
  isInvalid = false,
  textWrap = 'break-word',
  ...rest
}) => {
  const calculatedColor = isInvalid ? 'danger' : color;
  const theme = useEuiTheme();
  const styles = euiExpressionStyles(theme);
  const cssStyles = [styles.euiExpression, onClick && styles.isClickable, styles[color], isActive && styles.isActive.base, isActive && styles.isActive[color], display === 'columns' && styles.columns, textWrap === 'truncate' && styles.truncate];
  const descriptionStyles = euiExpressionDescriptionStyles(theme);
  const cssDescriptionStyles = [descriptionStyles.euiExpression__description, isInvalid ? descriptionStyles.danger : descriptionStyles[color], uppercase && descriptionStyles.isUppercase, textWrap === 'truncate' && descriptionStyles.truncate, display === 'columns' && descriptionStyles.columns];
  const valueStyles = euiExpressionValueStyles(theme);
  const cssValueStyles = [valueStyles.euiExpression__value, textWrap === 'truncate' && valueStyles.truncate, display === 'columns' && valueStyles.columns];
  const iconStyles = euiExpressionIconStyles(theme);
  const cssIconStyles = [iconStyles.euiExpression__icon, display === 'columns' && iconStyles.columns];
  const classes = classNames('euiExpression', className);
  const Component = onClick ? 'button' : 'span';
  const customWidth = display === 'columns' && descriptionWidth ? {
    flexBasis: descriptionWidth
  } : undefined;
  const invalidIcon = isInvalid ? <EuiIcon className="euiExpression__icon" type="warning" css={cssIconStyles} color={calculatedColor} /> : undefined;
  return <Component css={cssStyles} className={classes} onClick={onClick} {...rest}>
      <span {...descriptionProps} className={classNames('euiExpression__description', descriptionProps?.className)} css={[...cssDescriptionStyles, descriptionProps?.css]} style={{
      ...customWidth,
      ...descriptionProps?.style
    }}>
        {description}
      </span>{' '}
      {value && <span {...valueProps} className={classNames('euiExpression__value', valueProps?.className)} css={[...cssValueStyles, valueProps?.css]}>
          {value}
        </span>}
      {invalidIcon}
    </Component>;
};
EuiExpression.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * First part of the expression
     */
  /**
     * First part of the expression
     */
  description: PropTypes.node.isRequired,
  descriptionProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  /**
     * Second part of the expression
     */
  /**
     * Second part of the expression
     */
  value: PropTypes.node,
  valueProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  /**
     * Color of the `description`
     */
  /**
     * Color of the `description`
     */
  color: PropTypes.any,
  /**
     * Should the `description` auto-uppercase?
     */
  /**
     * Should the `description` auto-uppercase?
     */
  uppercase: PropTypes.bool,
  /**
     * Adds an solid border at the bottom
     */
  /**
     * Adds an solid border at the bottom
     */
  isActive: PropTypes.bool,
  /**
     * Turns the component into a button and adds an editable style border at the bottom
     */
  /**
     * Turns the component into a button and adds an editable style border at the bottom
     */
  onClick: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.func, PropTypes.func.isRequired]), PropTypes.func]),
  /**
     * Sets the display style for the expression. Defaults to `inline`
     */
  /**
     * Sets the display style for the expression. Defaults to `inline`
     */
  display: PropTypes.oneOf(["inline", "columns"]),
  /**
     * Forces color to display as `danger` and shows an `error` icon
     */
  /**
     * Forces color to display as `danger` and shows an `error` icon
     */
  isInvalid: PropTypes.bool,
  /**
     * Sets a custom width for the description when using the columns layout.
     * Set to a number for a custom width in `px`.
     * Set to a string for a custom width in custom measurement.
     * Defaults to `20%`
     */
  /**
     * Sets a custom width for the description when using the columns layout.
     * Set to a number for a custom width in `px`.
     * Set to a string for a custom width in custom measurement.
     * Defaults to `20%`
     */
  descriptionWidth: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  /**
     * Sets how to handle the wrapping of long text.
     */
  /**
     * Sets how to handle the wrapping of long text.
     */
  textWrap: PropTypes.oneOf(["break-word", "truncate"])
};
try {
  EuiExpression.__docgenInfo = {
    tags: {},
    filePath: '/app/packages/eui/src/components/expression/expression.tsx',
    description: '',
    displayName: 'EuiExpression',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      description: {
        defaultValue: null,
        description: 'First part of the expression',
        name: 'description',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      descriptionProps: {
        defaultValue: null,
        description: '',
        name: 'descriptionProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLSpanElement>'
        }
      },
      value: {
        defaultValue: null,
        description: 'Second part of the expression',
        name: 'value',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      valueProps: {
        defaultValue: null,
        description: '',
        name: 'valueProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLSpanElement>'
        }
      },
      color: {
        defaultValue: {
          value: 'success'
        },
        description: 'Color of the `description`',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"accent" | "primary" | "success" | "warning" | "danger" | "subdued"',
          value: [{
            value: '"accent"'
          }, {
            value: '"primary"'
          }, {
            value: '"success"'
          }, {
            value: '"warning"'
          }, {
            value: '"danger"'
          }, {
            value: '"subdued"'
          }]
        }
      },
      uppercase: {
        defaultValue: {
          value: 'true'
        },
        description: 'Should the `description` auto-uppercase?',
        name: 'uppercase',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isActive: {
        defaultValue: {
          value: 'false'
        },
        description: 'Adds an solid border at the bottom',
        name: 'isActive',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClick: {
        defaultValue: null,
        description: 'Turns the component into a button and adds an editable style border at the bottom',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement> | (MouseEventHandler<HTMLButtonElement> & MouseEventHandler<HTMLSpanElement>)'
        }
      },
      display: {
        defaultValue: {
          value: 'inline'
        },
        description: 'Sets the display style for the expression. Defaults to `inline`',
        name: 'display',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"inline" | "columns"',
          value: [{
            value: '"inline"'
          }, {
            value: '"columns"'
          }]
        }
      },
      isInvalid: {
        defaultValue: {
          value: 'false'
        },
        description: 'Forces color to display as `danger` and shows an `error` icon',
        name: 'isInvalid',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      descriptionWidth: {
        defaultValue: {
          value: '20%'
        },
        description: 'Sets a custom width for the description when using the columns layout.\n' + 'Set to a number for a custom width in `px`.\n' + 'Set to a string for a custom width in custom measurement.\n' + 'Defaults to `20%`',
        name: 'descriptionWidth',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number'
        }
      },
      textWrap: {
        defaultValue: {
          value: 'break-word'
        },
        description: 'Sets how to handle the wrapping of long text.',
        name: 'textWrap',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/expression/expression.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"truncate" | "break-word"',
          value: [{
            value: '"truncate"'
          }, {
            value: '"break-word"'
          }]
        }
      }
    },
    extendedInterfaces: ['ButtonHTMLAttributes', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}